import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateWhatsappRateInput, WhatsappRate, WhatsappUsage } from '@neuroedge/shared';
import { apiClient, type ApiClientError } from './client';

/** WhatsApp usage panel (ADR-041): delivered messages per number for a UTC month. */
export function useWhatsappUsage(month: string) {
  return useQuery<WhatsappUsage, ApiClientError>({
    queryKey: ['billing', 'whatsapp-usage', month],
    queryFn: () =>
      apiClient.get<WhatsappUsage>(`/billing/whatsapp-usage?month=${encodeURIComponent(month)}`),
    placeholderData: keepPreviousData,
  });
}

const RATES_KEY = ['admin', 'whatsapp-rates'] as const;

/** Admin → WhatsApp rates: Meta's rates per kind and market. */
export function useWhatsappRates(enabled = true) {
  return useQuery<WhatsappRate[], ApiClientError>({
    queryKey: RATES_KEY,
    queryFn: () => apiClient.get<WhatsappRate[]>('/admin/whatsapp-rates'),
    enabled,
  });
}

export function useCreateWhatsappRate() {
  const qc = useQueryClient();
  return useMutation<WhatsappRate, ApiClientError, CreateWhatsappRateInput>({
    mutationFn: (input) => apiClient.post<WhatsappRate>('/admin/whatsapp-rates', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: RATES_KEY }),
  });
}

export function useDeleteWhatsappRate() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => apiClient.delete<void>(`/admin/whatsapp-rates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: RATES_KEY }),
  });
}
